import Footer from '@bridebook/web/app-basic/components/footer/footer';
import { TopBar } from '@bridebook/web/app-basic/components/header';
import LandingPagesHead from '@bridebook/web/app-basic/components/landing/landing-pages-head';
import { showFullpageLoader } from '@bridebook/web/app-basic/lib/ab-tests/ab-tests-slice';
import { getLandingPageVariants } from '@bridebook/web/app-basic/lib/ab-tests/landing/get-landing-page-variants';
import { useLandingPageABTest } from '@bridebook/web/app-basic/lib/ab-tests/landing/use-landing-page-ab-test';
import { LandingPageVariants } from '@bridebook/web/app-basic/lib/ab-tests/types';
import { clickedOnLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/logged-out-page-click';
import { viewedLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/viewed-logged-out-page';
import { setAlternateLinks } from '@bridebook/web/app-basic/lib/app/app-slice';
import { getIsCordova } from '@bridebook/web/app-basic/lib/app/selectors';
import { setArticlesSupport } from '@bridebook/web/app-basic/lib/article/article-slice';
import {
  EdgeCacheTTL,
  basicGSSPWrapper,
} from '@bridebook/web/app-basic/lib/get-basic-server-side-props-wrapper';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isNil } from 'remeda';
import { Box } from '@bridebook/ui';
import { FaqSection } from '@bridebook/web/app-shared/components/landing/faq-section';
import HtmlPageWrapper from '@bridebook/web/app-shared/components/landing/html-page-wrapper';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { AppType } from '@bridebook/web/pages/_app';
import Error from '@bridebook/web/pages/_error';
import AppBarMobileBasic from '@bridebook/web/components/app-bar/app-bar-mobile/basic/app-bar-mobile-basic';
import {
  InvalidateWebflowCacheType,
  getWebflowHTML,
  getWebflowPathByPageId,
} from '@bridebook/web/lib/landing/get-webflow-html';
import { LandingPageIds } from '@bridebook/web/lib/landing/webflow-page-urls';
import { getAlternateLinksForWebflow } from '@bridebook/web/lib/utils/i18n-routing/get-alternate-links';
import { getArticlesMarketSupport } from '../../../../app-shared/lib/articles/get-articles-market-support';

export interface LandingServerSideProps {
  html: string | null;
  title: string | null;
  description: string | null;
  pageVariants: LandingPageVariants | null;
  pageId: LandingPageIds;
}

/**
 * Wrapper component to display page content
 */
const BasicLandingPagesWrapper: NextPage<LandingServerSideProps> = (props) => {
  const { pageId, title, description } = props;
  const html = useLandingPageABTest(props);
  const pageTitle = title || 'Bridebook';

  const dispatch = useDispatch();
  const { country } = useMarket();
  const isCordova = useSelector(getIsCordova);
  const router = useRouter();

  const onBackButtonClick = useCallback(() => {
    router.back();
  }, [router]);

  // Add event listener to startButton (cta: "Start today it's free") that is present on some webflow landing pages
  useEffect(() => {
    const startButton = document.getElementById('startButton');
    const getClickLocation = () => {
      if (pageId === LandingPageIds.localeHome) return 'homepage';
      if (pageId === LandingPageIds.weddingSuppliers) return 'supplierSearch';
      if (pageId === LandingPageIds.weddingPlanningTools) return 'planningTools';
      if (pageId === LandingPageIds.weddingIdeasInspiration) return 'weddingInspiration';
      if (pageId === LandingPageIds.toolsChecklist) return 'checklist';
      if (pageId === LandingPageIds.toolsBudget) return 'budget';
      if (pageId === LandingPageIds.toolsGuestlist) return 'guestList';
    };

    const clickedLocation = getClickLocation();

    if (!clickedLocation || !startButton) return;

    const handleStartButtonClick = () => {
      dispatch(
        clickedOnLoggedOutPage({
          clickedLocation,
          clickedSection: 'topButton',
          cta: "Start today it's free",
          supplierCountryCode: country,
        }),
      );
    };

    startButton.addEventListener('click', handleStartButtonClick);
    return () => {
      startButton.removeEventListener('click', handleStartButtonClick);
    };
  }, [country, dispatch, pageId]);

  useEffect(() => {
    dispatch(viewedLoggedOutPage({ title: pageTitle, selectedWeddingCountry: country }));
  }, [country, dispatch, pageTitle]);

  if (!html || isNil(pageId)) return <Error statusCode={404} />;

  return (
    <>
      {!isCordova ? (
        <TopBar />
      ) : (
        <>
          <AppBarMobileBasic title={pageTitle} onBack={onBackButtonClick} />
          <Box mt="53px" />
        </>
      )}
      <LandingPagesHead title={pageTitle} description={description} />
      <HtmlPageWrapper html={html} />
      <FaqSection pageId={pageId} />
      {!isCordova && <Footer />}
    </>
  );
};

BasicLandingPagesWrapper.AppType = AppType.Basic;

/**
 * Fetches translations and HTML file for the given landing
 */
export const getServerSideProps = basicGSSPWrapper<LandingServerSideProps>(
  ['landing', 'articles'],
  async ({ store, initialAppState, context }) => {
    const { market } = initialAppState;
    const { query, resolvedUrl, req } = context;
    const pageId = query?.id as unknown as LandingPageIds;

    if (
      // Check if pageId exists in a list of landing pages
      !LandingPageIds[pageId] ||
      // Do not allow direct entry to the landing page wrapper
      (resolvedUrl.startsWith(`${market.prefix}/landing/`) && resolvedUrl === req.url)
    ) {
      return {
        notFound: true,
      };
    }

    // Set alternate links for the landing page
    const alternate = getAlternateLinksForWebflow(pageId);
    store.dispatch(setAlternateLinks(alternate));

    // Fetch page HTML content from webflow
    const [webflow, pageVariants, articlesSupport] = await Promise.all([
      // Get original page content
      getWebflowHTML({
        webflowPath: getWebflowPathByPageId({ pageId, market }),
        market,
        invalidateCache: getInvalidateCacheType(query?.invalidateCache),
      }),
      // Get additional page variants for AB testing, if a test exists
      getLandingPageVariants({ pageId, market }),
      getArticlesMarketSupport(market.prefix),
    ]);

    store.dispatch(setArticlesSupport(articlesSupport));

    // Show full page loader if page contains any AB test variants
    if (pageVariants?.htmlVariants) {
      store.dispatch(showFullpageLoader(true));
    }

    return {
      props: {
        html: webflow?.html ?? null,
        title: webflow?.title ?? null,
        description: webflow?.description ?? null,
        pageVariants,
        pageId,
      },
    };
  },
  {
    vercelCacheControl: {
      maxAge: EdgeCacheTTL.HOUR,
      sMaxAge: EdgeCacheTTL.HOUR,
      staleWhileRevalidate: EdgeCacheTTL.WEEK,
    },
    cloudflareCacheControl: {
      maxAge: EdgeCacheTTL.TEN_MINUTES,
      sMaxAge: EdgeCacheTTL.TEN_MINUTES,
      staleWhileRevalidate: EdgeCacheTTL.HOUR,
    },
  },
);

const getInvalidateCacheType = (
  invalidateCache: string | string[] | undefined,
): InvalidateWebflowCacheType | undefined => {
  if (invalidateCache === undefined) {
    return undefined; // will not clear cache
  } else if (invalidateCache === '*') {
    return InvalidateWebflowCacheType.CLEAR_ALL_CACHE; // will clear all pages from the cache if ?invalidateCache=* is added to url
  } else {
    return InvalidateWebflowCacheType.CLEAR_PAGE_CACHE; // will clear only currently requested page from cache ?invalidateCache is added to url
  }
};

export default BasicLandingPagesWrapper;
